import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { Resource } from "react-admin";
import { useTokenOps } from "../components/TokenContext";
import { hostname } from "../providers/api";
import { CarbonAdminPage, CarbonDataTable, CarbonSection, CarbonSelectInput, CarbonTextInput, CarbonFilterBar } from "./corporateCarbon/CarbonAdminPage";
import { ReportingRevisionFilter } from './corporateCarbon/ReportingRevisionFilter';

const PAGE_SIZE = 25;

const fetchAudit = async (token, page, filters) => {
  const query = new URLSearchParams(Object.entries(filters).filter(([, value]) => value));
  const response = await fetch(
    `${hostname}/corporate-carbon/audit?page=${page}&perPage=${PAGE_SIZE}${query.toString() ? `&${query}` : ''}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const body = await response.json();
  if (!response.ok)
    throw new Error(body.message || "Audit history is unavailable.");
  if (!body || !Array.isArray(body.data) || !Number.isInteger(body.total))
    throw new Error("Invalid audit history response.");
  return body;
};

const Snapshot = ({ label, value }) => (
  <div>
    <Typography variant="subtitle2">{label}</Typography>
    <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
      {JSON.stringify(value, null, 2)}
    </pre>
  </div>
);

export const CarbonAuditTimeline = ({ accessToken }) => {
  const tokenOps = useTokenOps();
  const token = accessToken || tokenOps.account?.accessToken;
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const filter = (key, value) => { setPage(1); setFilters(current => ({ ...current, [key]: value })); };
  const [result, setResult] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    if (!token) {
      setError(
        new Error("An active account token is required to load audit history.")
      );
      setLoading(false);
      return () => {
        active = false;
      };
    }
    fetchAudit(token, page, filters)
      .then((next) => {
        if (active) setResult(next);
      })
      .catch((nextError) => {
        if (active) setError(nextError);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [page, token, filters]);

  useEffect(() => { setPage(1); setFilters(current => Object.keys(current).length ? {} : current); setResult({ data: [], total: 0 }); }, [token]);

  return (
    <CarbonAdminPage
      title="Carbon audit history"
      description="Review the immutable account activity trail and inspect recorded changes."
    >
        <CarbonFilterBar label="Audit filters">
          <CarbonSelectInput label="Action" name="action" value={filters.action || ''} emptyText="All actions" choices={['created','updated','archived','finalized','superseded','linked','replaced','download_opened','recalculated','exported','imported'].map(id => ({ id, name: id.replace(/_/g, ' ') }))} onChange={event => filter('action', event.target.value)} />
          <CarbonSelectInput label="Record type" name="entityType" value={filters.entityType || ''} emptyText="All types" choices={['CarbonProfile','CarbonSite','CarbonInventory','CarbonInventoryRevision','InventoryBoundarySite','CarbonActivity','CarbonEvidenceDocument','CarbonCalculationRun','CarbonExportRecord','CarbonImportBatch'].map(id => ({ id, name: id.replace(/^Carbon/, '').replace(/([a-z])([A-Z])/g, '$1 $2') }))} onChange={event => filter('entityType', event.target.value)} />
          <CarbonTextInput label="Actor name or email" name="actor" value={filters.actor || ''} onChange={event => filter('actor', event.target.value)} />
          <CarbonTextInput label="From" name="from" type="date" value={filters.from || ''} onChange={event => filter('from', event.target.value)} />
          <CarbonTextInput label="To" name="to" type="date" value={filters.to || ''} onChange={event => filter('to', event.target.value)} />
          <ReportingRevisionFilter token={token} value={filters.revisionId || ''} onChange={value => filter('revisionId', value)} inventoryId={filters.inventoryId || ''} onInventoryChange={value => { setPage(1); setFilters(current => ({ ...current, inventoryId: value, revisionId: '' })); }} />
        </CarbonFilterBar>
        {loading && (
          <div role="status">
            <CircularProgress size={20} /> Loading audit history...
          </div>
        )}
        {!loading && error && (
          <Typography role="alert" color="error">
            {error.message}
          </Typography>
        )}
        {!loading && !error && result.data.length === 0 && (
          <Typography>No audit events found.</Typography>
        )}
        {!loading && !error && result.data.length > 0 && (
          <CarbonSection title="Audit events" description="Immutable changes recorded for the active account. Expand an event to inspect its before-and-after snapshots.">
            <CarbonDataTable label="Carbon audit events">
              <TableHead><TableRow><TableCell>Action</TableCell><TableCell>Record</TableCell><TableCell>Actor</TableCell><TableCell>Date</TableCell><TableCell>Reason and details</TableCell></TableRow></TableHead>
              <TableBody>{result.data.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{entry.action}</TableCell>
                  <TableCell>{entry.recordLabel || entry.entityType.replace(/^Carbon/, '').replace(/([a-z])([A-Z])/g, '$1 $2')}</TableCell>
                  <TableCell>{entry.actorLabel || 'User not recorded'}</TableCell>
                  <TableCell><time dateTime={entry.occurredAt}>{new Date(entry.occurredAt).toLocaleString()}</time></TableCell>
                  <TableCell>
                    {entry.reason && <Typography variant="body2">{entry.reason}</Typography>}
                    <details>
                      <summary>Before and after</summary>
                      <Snapshot label="Before" value={entry.before} />
                      <Snapshot label="After" value={entry.after} />
                    </details>
                  </TableCell>
                </TableRow>
              ))}</TableBody>
            </CarbonDataTable>
          </CarbonSection>
        )}
        {!loading && !error && (
          <nav aria-label="Audit history pagination" style={{ marginTop: 16 }}>
            <Button
              aria-label="Previous page"
              disabled={page === 1}
              onClick={() => setPage((value) => value - 1)}
            >
              Previous
            </Button>
            <Button
              aria-label="Next page"
              disabled={page * PAGE_SIZE >= result.total}
              onClick={() => setPage((value) => value + 1)}
            >
              Next
            </Button>
          </nav>
        )}
    </CarbonAdminPage>
  );
};

const carbonAuditResource = () => (
  <Resource
    name="carbonAudit"
    options={{ label: "Audit history", menuGroup: "Carbon Accounting" }}
    list={() => <CarbonAuditTimeline />}
  />
);

export default carbonAuditResource;
